import React, { useEffect, useState } from 'react';
import { Stack, Typography, useTheme } from '@mui/material';
import { useAppDispatch, useAppSelector } from 'hooks/redux.hooks';
import { ClientPaginatedTable } from 'components/table';
import * as TableConfig from './tablesConfig';
import { selectTranslations } from 'state/reducers/translation.reducers';
import { updateBankDigitalDepositAvailability } from 'services/global.service';
import { DialogBox } from 'components/DialogBox';

type IProps = {
	bankList: Record<string, any>[];
};

export const DigitalDepositBankAvailabilityTable: React.FC<IProps> = ({ bankList }: IProps) => {
	const t = useAppSelector(selectTranslations);
	const theme = useTheme();
	const dispatch = useAppDispatch();
	const [tableData, setTableData] = useState<Record<string, any>[]>([]);
	const [pendingBankChange, setPendingBankChange] = useState<Record<string, any> | null>(null);

	useEffect(() => {
		setDefaultModifiedBanks();
	}, [bankList]);

	const getDigitalDepositBanks = () => {
		return bankList
			.filter((bank: any) => bank.isDigitalDepositSupported === true)
			.sort((firstBank: any, secondBank: any) => Number(firstBank.bankCode) - Number(secondBank.bankCode));
	};

	const setDefaultModifiedBanks = () => {
		const modifiedBanks = getDigitalDepositBanks().map((bank: any) => {
			return { ...bank, editable: false };
		});
		setTableData(modifiedBanks);
	};

	const handleEditButtonClick = (bankId: number) => {
		const modifiedBanks = getDigitalDepositBanks().map((bank: any) => {
			return { ...bank, editable: bankId === bank.id ? true : false };
		});
		setTableData(modifiedBanks);
	};

	const saveBankChanges = (newBankData: any) => {
		setPendingBankChange(newBankData);
	};

	const approveBankChanges = () => {
		if (!pendingBankChange) {
			return;
		}

		const { id, isDigitalDepositTemporarilyUnavailable } = pendingBankChange;

		setDefaultModifiedBanks();
		dispatch(
			updateBankDigitalDepositAvailability({ bankId: id, isDigitalDepositTemporarilyUnavailable }, bankList)
		);
		setPendingBankChange(null);
	};

	const closeBankChangesConfirmation = () => {
		setDefaultModifiedBanks();
		setPendingBankChange(null);
	};

	const cancelBankChanges = () => {
		setDefaultModifiedBanks();
	};

	const updateModifiedBanks = (field: any, fieldValue: any, changedBank: any) => {
		const modifiedBanks = tableData.map((bank: any) => {
			return {
				...bank,
				editable: changedBank.id === bank.id ? true : false,
				isDigitalDepositTemporarilyUnavailable:
					changedBank.id === bank.id
						? field === 'isDigitalDepositTemporarilyUnavailable'
							? fieldValue
							: changedBank.isDigitalDepositTemporarilyUnavailable
						: bank.isDigitalDepositTemporarilyUnavailable,
			};
		});
		setTableData(modifiedBanks);
	};

	return (
		<Stack spacing={2}>
			{pendingBankChange && (
				<DialogBox
					openDialog
					handleClose={closeBankChangesConfirmation}
					handleApprove={approveBankChanges}
					handleReject={closeBankChangesConfirmation}
					title={t('page.digital_deposit_bank_availability.approveQuestionTitle')}
				>
					<Typography>
						{t('page.digital_deposit_bank_availability.approveQuestionSubtitle').replace(
							'%%bankName%%',
							pendingBankChange.bankName.trim()
						)}
					</Typography>
				</DialogBox>
			)}
			<Typography variant="subtitle" color={theme.palette.root.grey}>
				{tableData.length + ' ' + t('messages.results')}
			</Typography>
			<ClientPaginatedTable
				data={TableConfig.tableData(tableData)}
				columns={TableConfig.buildColumns(
					t,
					handleEditButtonClick,
					saveBankChanges,
					cancelBankChanges,
					updateModifiedBanks
				)}
			/>
		</Stack>
	);
};
